import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Pack, PackProgress } from "../domain/types";
import { getPacksByMode, getPackProgress } from "../db";
import { useLanguage } from "../state/language";
import { modeName } from "../domain/i18n";
import type { ModeId } from "../domain/enums";
import { modeStyle } from "../components/modeTheme";
import { Button } from "../components/Button";

interface Row extends Pack {
  progress: PackProgress | undefined;
}

export default function Packs() {
  const { modeId } = useParams<{ modeId: ModeId }>();
  const nav = useNavigate();
  const { lang, t } = useLanguage();
  const [rows, setRows] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState(false);
  const language = lang ?? "uk";

  useEffect(() => {
    if (!modeId) return;
    (async () => {
      const list = await getPacksByMode(modeId);
      const enriched: Row[] = await Promise.all(
        list
          .sort((a, b) => b.sortPriority - a.sortPriority)
          .map(async (p) => ({
            ...p,
            progress: await getPackProgress(p.id),
          }))
      );
      setRows(enriched);
      setLoaded(true);
    })();
  }, [modeId]);

  if (!modeId) return null;

  return (
    <div className="pp-screen px-5" style={modeStyle(modeId)}>
      <div className="pp-aurora" aria-hidden />
      <header className="flex items-center justify-between pt-4">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          ← {t("cta_back")}
        </Button>
        <h2 className="font-display text-xl font-bold">{modeName(language, modeId)}</h2>
        <div className="w-12" />
      </header>
      <h3 className="font-display text-3xl font-extrabold mt-6">{t("packs_title")}</h3>

      <div className="mt-6 flex flex-col gap-3">
        {loaded && rows.length === 0 && (
          <div className="opacity-70 text-center mt-12">{t("no_packs")}</div>
        )}
        {rows.map((p) => {
          const status = p.progress?.status ?? "not_started";
          const pct = p.progress?.progressPct ?? 0;
          const title = p.titleByLang[language] || p.titleByLang.uk;
          return (
            <button
              key={p.id}
              onClick={() => nav(`/pack/${p.id}`)}
              className="pp-card-shell text-left active:scale-[0.99] transition"
            >
              <div className="flex items-center justify-between">
                <div className="font-display text-xl font-bold">{title}</div>
                <span className="text-xs opacity-70 tabular-nums">{p.totalCards}</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span
                  className={`px-2 py-1 rounded-full border ${
                    status === "completed"
                      ? "border-emerald-400/60 text-emerald-300"
                      : status === "in_progress"
                        ? "border-yellow-300/60 text-yellow-200"
                        : "border-white/30 text-white/70"
                  }`}
                >
                  {status === "completed"
                    ? t("pack_status_completed")
                    : status === "in_progress"
                      ? t("pack_status_in_progress")
                      : t("pack_status_not_started")}
                </span>
                {status === "in_progress" && (
                  <div className="flex-1 h-1.5 bg-white/15 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
